import { WorkspaceObjectRefBudget } from '@affine/core/modules/streamlain/copilot/workspace-view-budget';
import {
  WORKSPACE_VIEW_MAX_BYTES,
  WORKSPACE_VIEW_MAX_REF_VISITS,
  type WorkspaceContextIssue,
  type WorkspaceViewReadResult,
  type WorkspaceViewRevision,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';

import type {
  ChatContextAttachmentPatch,
  ChatContextManagerPort,
  ChatContextSelection,
  ChatContextSendPreparation,
  ChatContextValue,
  NativeContextBinding,
  WorkspaceViewContextReader,
  WorkspaceViewPlace,
  WorkspaceViewSelection,
} from './type';
import { isWorkflowContextReference } from './type';

export class ChatContextReadError extends Error {
  constructor(
    readonly reason: WorkspaceContextIssue,
    readonly diagnostic?: Readonly<Record<string, unknown>>
  ) {
    super(reason);
  }
}
/** Keep the original throw site when presenting a localized send failure. */
export function reportChatContextError(
  error: Error & { reason: WorkspaceContextIssue },
  message: string
): Error {
  console.error('Chat context send failed', error, error.stack, {
    reason: error.reason,
    ...(error instanceof ChatContextReadError ? error.diagnostic : {}),
  });
  return new Error(message, { cause: error });
}

function issue(error: unknown): WorkspaceContextIssue {
  const reason =
    error instanceof Error && 'reason' in error ? error.reason : undefined;
  return [
    'unavailable',
    'stale',
    'blocked',
    'offline',
    'invalid',
    'oversized',
    'timeout',
  ].includes(String(reason))
    ? (reason as WorkspaceContextIssue)
    : 'unavailable';
}
function sameScope(a: WorkspaceViewScope, b: WorkspaceViewScope) {
  return a.workspaceId === b.workspaceId && a.viewId === b.viewId;
}
/**
 * A page that is still settling while it is read (late mounts, its title
 * arriving) moves the revision of its view, and the read comes back stale.
 * While the same view stands at the same route as at the first read, it is
 * read again, so the first send after opening a page carries the page as it
 * is now. Any navigation, to another view or to another route in this view,
 * stays stale.
 */
function samePlace(a: WorkspaceViewPlace | null, b: WorkspaceViewPlace | null) {
  return (
    !!a &&
    !!b &&
    sameScope(a, b) &&
    a.route === b.route &&
    a.captured.every(source => b.captured.includes(source))
  );
}
const VIEW_SETTLE_READS = 5;

async function readSettled(
  reader: WorkspaceViewContextReader,
  selection: WorkspaceViewSelection | undefined,
  signal: AbortSignal
): Promise<WorkspaceViewReadResult> {
  const start = reader.place();
  for (let round = 1; ; round++) {
    signal.throwIfAborted();
    const result = reader.collect
      ? await reader.collect(selection, signal)
      : reader(selection);
    if (result.ok || result.reason !== 'stale' || round >= VIEW_SETTLE_READS)
      return result;
    signal.throwIfAborted();
    if (!samePlace(start, reader.place()) || !reader.settle) return result;
    if (!(await reader.settle(signal))) return result;
    signal.throwIfAborted();
    if (!samePlace(start, reader.place())) return result;
  }
}
function ids(binding: NativeContextBinding) {
  return binding.kind === 'workflow' ? binding.nodeIds : binding.objectIds;
}
function nativeOwner(
  selection: Extract<ChatContextSelection, { kind: 'native' }>
) {
  const b = selection.binding;
  return JSON.stringify(
    b.kind === 'workflow'
      ? [
          selection.scope.workspaceId,
          selection.scope.viewId,
          b.kind,
          b.workspaceId,
          b.workflowId,
          b.version,
          b.modelRevision,
          selection.sourceGeneration,
        ]
      : [
          selection.scope.workspaceId,
          selection.scope.viewId,
          b.kind,
          b.workspaceId,
          b.docId,
          b.storeGeneration,
          selection.sourceGeneration,
        ]
  );
}
function appendPatch(
  context: ChatContextValue,
  patch: ChatContextAttachmentPatch
) {
  // Only declared attachment fields can enter a native projection. Functions and
  // reference metadata never enter the canonical attachment payload.
  for (const key of [
    'images',
    'attachments',
    'docs',
    'databaseAttachments',
    'automationAttachments',
    'workflowAttachments',
    'codeSelections',
    'mailAttachments',
    'pdfAttachments',
    'pdfContextAttachments',
    'canvasSelections',
  ] as const) {
    const entries = patch[key];
    if (entries)
      Object.assign(context, { [key]: [...(context[key] ?? []), ...entries] });
  }
  for (const key of [
    'quote',
    'markdown',
    'combinedElementsMarkdown',
  ] as const) {
    const text = patch[key];
    if (text)
      Object.assign(context, {
        [key]: context[key] ? `${context[key]}\n${text}` : text,
      });
  }
  if (patch.snapshot !== undefined) context.snapshot = patch.snapshot;
  if (patch.html !== undefined) context.html = patch.html;
}

/** A port over the existing Content value. No copied payload store or cache. */
export class ChatContextManager implements ChatContextManagerPort {
  constructor(
    private readonly get: () => ChatContextValue,
    private readonly set: (
      patch: ChatContextAttachmentPatch &
        Pick<ChatContextValue, 'contextSelections'>
    ) => void,
    private readonly getReader: () => WorkspaceViewContextReader | undefined
  ) {}

  private publish(
    selections: readonly ChatContextSelection[],
    patch: ChatContextAttachmentPatch = {}
  ) {
    this.set({ ...patch, contextSelections: selections });
  }
  private admit(selections: readonly ChatContextSelection[]): void {
    if (selections.length > WORKSPACE_VIEW_MAX_REF_VISITS)
      throw new ChatContextReadError('oversized');
    const budget = new WorkspaceObjectRefBudget({
      kind: 'chat-context',
      id: 'admission',
    });
    for (const s of selections) {
      if (s.dismissed) continue;
      const strings = [
        s.key,
        s.sourceKey,
        s.label,
        s.sourceGeneration,
        s.scope.workspaceId,
        s.scope.viewId,
      ];
      if (
        s.binding.workspaceId !== s.scope.workspaceId ||
        ('viewId' in s.binding && s.binding.viewId !== s.scope.viewId)
      )
        throw new ChatContextReadError('invalid');
      if (s.kind === 'native') {
        strings.push(
          ...(s.binding.kind === 'workflow'
            ? [s.binding.workflowId]
            : [s.binding.docId, s.binding.storeGeneration])
        );
        if (
          s.binding.kind === 'workflow' &&
          (!Number.isSafeInteger(s.binding.modelRevision) ||
            (s.binding.version !== null &&
              !Number.isSafeInteger(s.binding.version)))
        )
          throw new ChatContextReadError('invalid');
      }
      for (const text of strings) {
        if (typeof text !== 'string') throw new ChatContextReadError('invalid');
        if (text.length > WORKSPACE_VIEW_MAX_BYTES)
          throw new ChatContextReadError('oversized');
      }
      if (s.kind === 'native') {
        const selected = ids(s.binding);
        if (!selected.length) throw new ChatContextReadError('unavailable');
        if (selected.length > WORKSPACE_VIEW_MAX_REF_VISITS)
          throw new ChatContextReadError('oversized');
        for (const id of selected) {
          if (typeof id !== 'string' || !id)
            throw new ChatContextReadError('invalid');
          if (id.length > WORKSPACE_VIEW_MAX_BYTES)
            throw new ChatContextReadError('oversized');
          if (
            !budget.visit() ||
            !budget.add({
              kind: s.binding.kind,
              id: JSON.stringify([nativeOwner(s), id]),
              attributes: { label: s.label },
            })
          )
            throw new ChatContextReadError('oversized');
        }
      } else if (
        !budget.visit() ||
        !budget.add({
          ...s.ref,
          id: JSON.stringify([
            s.scope.workspaceId,
            s.scope.viewId,
            s.sourceKey,
            s.sourceGeneration,
            s.ref.kind,
            s.ref.id,
          ]),
          attributes: {
            ...s.ref.attributes,
            ...s.binding,
            label: s.label,
            key: s.key,
          },
        })
      )
        throw new ChatContextReadError('oversized');
    }
  }
  applyAutomatic(
    sourceKey: string,
    revision: WorkspaceViewRevision,
    patch: ChatContextAttachmentPatch,
    selections: readonly ChatContextSelection[]
  ): void {
    const current = this.get().contextSelections;
    const previous = current.filter(
      s => s.provenance === 'automatic' && s.sourceKey === sourceKey
    );
    if (
      previous.some(
        s =>
          s.dismissed &&
          s.revision?.epoch === revision.epoch &&
          s.revision.sequence === revision.sequence
      )
    )
      return;
    const next = [
      ...current.filter(
        s => s.provenance !== 'automatic' || s.sourceKey !== sourceKey
      ),
      ...selections.map(s => ({
        ...s,
        sourceKey,
        revision,
        provenance: 'automatic' as const,
        dismissed: false,
      })),
    ];
    this.admit(next);
    this.publish(next, patch);
  }
  addExplicit(
    selection: ChatContextSelection
  ): { ok: true } | { ok: false; reason: WorkspaceContextIssue } {
    try {
      if (selection.provenance !== 'explicit' || selection.dismissed)
        return { ok: false, reason: 'invalid' };
      selection.assertCurrent();
      const current = this.get().contextSelections;
      const next = [
        ...current.filter(
          s => !(s.provenance === 'explicit' && s.key === selection.key)
        ),
        selection,
      ];
      this.admit(next);
      this.publish(next);
      return { ok: true };
    } catch (error) {
      return { ok: false, reason: issue(error) };
    }
  }
  remove(keys: readonly string[]): void {
    const removed = new Set(keys);
    this.publish(
      this.get().contextSelections.flatMap(s =>
        !removed.has(s.key)
          ? [s]
          : s.provenance === 'automatic'
            ? [{ ...s, dismissed: true }]
            : []
      )
    );
  }
  isSelected(key: string): boolean {
    return this.get().contextSelections.some(
      s => s.key === key && !s.dismissed
    );
  }
  invalidateSource(sourceKey: string, generation: string): void {
    this.publish(
      this.get().contextSelections.filter(
        s => s.sourceKey !== sourceKey || s.sourceGeneration !== generation
      )
    );
  }
  invalidateScope(scope: WorkspaceViewScope): number {
    const selections = this.get().contextSelections;
    const kept = selections.filter(s => !sameScope(s.scope, scope));
    this.publish(kept);
    return selections.filter(s => !kept.includes(s) && !s.dismissed).length;
  }

  async prepareForSend(
    signal: AbortSignal,
    getReader = this.getReader
  ): Promise<ChatContextSendPreparation> {
    signal.throwIfAborted();
    const source = this.get();
    const selections = source.contextSelections.filter(s => !s.dismissed);
    const assertCurrent = () => {
      signal.throwIfAborted();
      const current = this.get().contextSelections;
      for (const s of selections) {
        if (!current.includes(s) || s.dismissed)
          throw new ChatContextReadError('stale', {
            phase: 'selection-presence',
            source: s.sourceKey,
            expectedRevision: s.revision,
          });
        try {
          s.assertCurrent();
        } catch (error) {
          throw new ChatContextReadError(issue(error), {
            phase: 'selection-current',
            source: s.sourceKey,
            expectedRevision: s.revision,
            originalStack: error instanceof Error ? error.stack : undefined,
          });
        }
      }
    };
    assertCurrent();
    const context = { ...source };
    const groups = new Map<
      string,
      {
        selection: Extract<ChatContextSelection, { kind: 'native' }>;
        ids: Set<string>;
      }
    >();
    for (const s of selections) {
      if (s.kind !== 'native') continue;
      const key = nativeOwner(s),
        group = groups.get(key);
      if (group) {
        for (const id of ids(s.binding)) group.ids.add(id);
        if (s.provenance === 'explicit') group.selection = s;
      } else groups.set(key, { selection: s, ids: new Set(ids(s.binding)) });
    }
    // Managed automatic projections are replaced by their single native read.
    const managedWorkflows = source.contextSelections.filter(
      s => s.kind === 'native' && s.provenance === 'automatic'
    );
    context.workflowAttachments = source.workflowAttachments.filter(
      attachment =>
        !attachment.autoSelected ||
        !managedWorkflows.some(
          s =>
            s.kind === 'native' &&
            s.binding.kind === 'workflow' &&
            attachment.workflowId === s.binding.workflowId &&
            (attachment.version ?? null) === s.binding.version
        )
    );
    for (const group of groups.values()) {
      assertCurrent();
      const selection = group.selection,
        binding = selection.binding;
      const automatic =
        binding.kind === 'workflow' &&
        source.workflowAttachments.find(attachment => {
          if (
            !attachment.autoSelected ||
            !isWorkflowContextReference(attachment)
          )
            return false;
          const native = attachment.nativeBinding;
          return (
            native?.workspaceId === binding.workspaceId &&
            native.workflowId === binding.workflowId &&
            native.version === binding.version &&
            native.modelRevision === binding.modelRevision &&
            attachment.sourceGeneration === selection.sourceGeneration
          );
        });
      if (
        automatic &&
        isWorkflowContextReference(automatic) &&
        automatic.materializeWithPinnedIds
      ) {
        // Preserve the native automatic selection and its full-domain limit; union
        // only at the cooperative body reader, never by copying it on publication.
        context.workflowAttachments = context.workflowAttachments.filter(
          value => value !== automatic
        );
        appendPatch(context, {
          workflowAttachments: [
            await automatic.materializeWithPinnedIds(signal, [...group.ids]),
          ],
        });
      } else
        appendPatch(
          context,
          await selection.materialize(signal, [...group.ids], assertCurrent)
        );
      assertCurrent();
    }
    return {
      context,
      assertCurrent,
      readForSend: async () => {
        assertCurrent();
        const refs: WorkspaceViewSelection['refs'][number][] = [];
        const elements: WorkspaceViewSelection['elements'][number][] = [];
        const readKeys = new Set<string>();
        for (const s of selections) {
          if (
            s.kind === 'native' ||
            readKeys.has(
              JSON.stringify([
                s.scope.workspaceId,
                s.scope.viewId,
                s.sourceKey,
                s.sourceGeneration,
                s.ref.kind,
                s.ref.id,
              ])
            )
          )
            continue;
          readKeys.add(
            JSON.stringify([
              s.scope.workspaceId,
              s.scope.viewId,
              s.sourceKey,
              s.sourceGeneration,
              s.ref.kind,
              s.ref.id,
            ])
          );
          const value = await s.read(signal);
          assertCurrent();
          if (
            !value.refs.some(
              ref => ref.kind === s.ref.kind && ref.id === s.ref.id
            )
          )
            throw new ChatContextReadError('unavailable');
          if (
            refs.length + value.refs.length > WORKSPACE_VIEW_MAX_REF_VISITS ||
            elements.length + value.elements.length >
              WORKSPACE_VIEW_MAX_REF_VISITS
          )
            throw new ChatContextReadError('oversized');
          refs.push(...value.refs);
          elements.push(...value.elements);
        }
        const reader = getReader();
        if (refs.length && !reader)
          throw new ChatContextReadError('unavailable');
        const readPlace = reader?.place();
        let workspaceView;
        try {
          const selection = refs.length ? { refs, elements } : undefined;
          workspaceView = reader
            ? await readSettled(reader, selection, signal)
            : undefined;
        } catch (error) {
          signal.throwIfAborted();
          if (error instanceof ChatContextReadError && error.reason === 'stale')
            throw error;
          if (refs.length) throw new ChatContextReadError('unavailable');
          // An optional automatic envelope remains best effort, as before.
          // This failure carries no snapshot and grants no context authority.
          workspaceView = {
            ok: false as const,
            reason: 'unavailable' as const,
            scope: selections[0]?.scope ?? { workspaceId: '', viewId: '' },
          };
        }
        assertCurrent();
        if (
          reader?.collect &&
          workspaceView &&
          !workspaceView.ok &&
          workspaceView.reason === 'stale'
        )
          throw new ChatContextReadError('stale', {
            phase: 'collect',
            expected: readPlace,
            current: reader.place(),
            scope: workspaceView.scope,
          });
        if (refs.length) {
          if (!workspaceView?.ok)
            throw new ChatContextReadError(
              workspaceView?.reason ?? 'unavailable'
            );
          if (selections.some(s => !sameScope(s.scope, workspaceView.snapshot)))
            throw new ChatContextReadError('stale');
        }
        const assertReadCurrent = () => {
          assertCurrent();
          if (
            workspaceView?.ok &&
            reader?.isCurrent &&
            !reader.isCurrent(workspaceView.snapshot)
          )
            throw new ChatContextReadError('stale', {
              phase: 'send-boundary',
              source: workspaceView.snapshot.surfaceId,
              expectedRevision: workspaceView.snapshot.revision,
              current: reader.place(),
            });
        };
        assertReadCurrent();
        return { context, workspaceView, assertCurrent: assertReadCurrent };
      },
    };
  }
  async readForSend(signal: AbortSignal) {
    return (await this.prepareForSend(signal)).readForSend();
  }
}
